import { trackError } from '~/services/logger/error.utils';
import { useRootStore } from '~/stores/useRootStore';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { useState } from 'react';
import { TAnalyticsEventAction } from '~/services/analytics/analytics.types';
import { getClickShareTemplateSource } from '~/components/Templates/templates.utils';
import useAnalytics from '~/common/useAnalytics';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { useModal } from '~/providers/modal.provider';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { PublicTemplateConfigurationProps } from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModal';
import Web24 from '@honeybook/icons/dist/icons/Web24';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';

export const usePublishedTemplate = ({
    publicConfiguration
}: PublicTemplateConfigurationProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { track } = useAnalytics();
    const { toastStore } = useGlobalMX();
    const { closeModal } = useModal();
    const { isMediumUp } = useWindowSize();
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const { t: tPrePublish } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.pre_publish'
    });
    const {
        templatesUiStore: {
            selectedTemplate,
            updateTemplatePublicConfiguration,
            closeShareModal
        },
        templatesCtrl,
        flowStore
    } = useRootStore();

    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

    const isPublished = publicConfiguration?.published_template;

    const handleActivateToggleChange = () => {
        if (isPublished) {
            trackEvent({
                event: 'deactivate share link modal',
                action: 'load'
            });
            setIsDeactivateModalOpen(true);
        } else {
            generateLink();
        }
    };

    const generateLink = async () => {
        track('generate share link', {
            action: 'click',
            source: getClickShareTemplateSource(),
            template_id: selectedTemplate?._id
        });
        try {
            setIsLoading(true);
            const res = await templatesCtrl.publishTemplate(
                selectedTemplate?._id
            );
            updateTemplatePublicConfiguration(res);
            if (flowStore.flows[res._id]) {
                flowStore.updateFlow(res._id, {
                    public_configuration: res.public_configuration
                });
            }
            if (isMediumUp) {
                toastStore.add({
                    text: tPrePublish('success_toast'),
                    type: ToastType.Success,
                    icon: Web24
                });
            }
        } catch (error) {
            trackError({
                error,
                message: '[Public Template] An error on sharing template',
                context: { flowId: selectedTemplate?._id }
            });
            toastStore.add({
                type: ToastType.Error,
                text: tPrePublish('publish_error')
            });
        } finally {
            setIsLoading(false);
        }
    };

    const tracking_prefix = '[Public Template]';

    const trackEvent = ({
        event,
        action,
        additionalData
    }: {
        event: string;
        action?: TAnalyticsEventAction;
        additionalData?: {};
    }) => {
        track(event, {
            action: action || 'click',
            source: getClickShareTemplateSource(),
            template_id: selectedTemplate._id,
            ...additionalData
        });
    };

    const changeConfiguration = async ({
        configType,
        state,
        setLocalState
    }: {
        configType: string;
        state: boolean;
        setLocalState: (value: boolean) => void;
    }) => {
        try {
            const res = await templatesCtrl.changePublicTemplateConfiguration(
                selectedTemplate?._id,
                configType,
                state
            );
            updateTemplatePublicConfiguration(res);
            if (flowStore.flows[res._id]) {
                flowStore.updateFlow(res._id, {
                    public_configuration: res.public_configuration
                });
            }
            setLocalState(state);
        } catch (error) {
            trackError({
                error,
                message: `${tracking_prefix} An error on changing public template configuration`
            });
        }
    };

    const deactivateLink = async () => {
        try {
            trackEvent({ event: 'deactivate link' });
            setIsLoading(true);
            const res = await templatesCtrl.deactivateTemplate(
                selectedTemplate?._id
            );
            updateTemplatePublicConfiguration(res);
            if (flowStore.flows[res._id]) {
                flowStore.updateFlow(res._id, {
                    public_configuration: null
                });
            }
            toastStore.add({
                type: ToastType.Info,
                text: t('deactivate_modal.deactivate_success_toast'),
                icon: false
            });
            closeModal('PublicTemplateConfiguration');
            closeShareModal();
        } catch (error) {
            trackError({
                error,
                message: `${tracking_prefix} An error on deactivating public template`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const copyLink = (url: string) => {
        navigator.clipboard.writeText(url);
        toastStore.add({
            type: ToastType.Success,
            text: t('copy_success')
        });
        trackEvent({ event: 'copy share link' });
    };

    return {
        changeConfiguration,
        deactivateLink,
        copyLink,
        isLoading,
        trackEvent,
        isDeactivateModalOpen,
        handleActivateToggleChange,
        isPublished,
        setIsDeactivateModalOpen
    };
};
